import type { ExamMode, Rank } from "../shared/contracts";

export type IntroStage =
  | { kind: "HERO" }
  | { kind: "RANK"; rank: Rank };

export type IntroDirection = "UP" | "DOWN";
export type IntroBoundary = "NONE" | "BLOCKED_BEFORE" | "EXIT_AFTER";

export type IntroStep = {
  stage: IntroStage;
  boundary: IntroBoundary;
};

const DOUBLE_RANKS: Rank[] = ["NEW", "ELITE", "ACE", "PEAK"];
const SINGLE_RANKS: Rank[] = ["ELITE", "ACE", "PEAK"];

export function ranksForMode(mode: ExamMode) {
  return mode === "DOUBLE" ? DOUBLE_RANKS : SINGLE_RANKS;
}

export function firstRankStage(mode: ExamMode): IntroStage {
  return { kind: "RANK", rank: ranksForMode(mode)[0] };
}

export function normalizeStageForMode(stage: IntroStage, mode: ExamMode): IntroStage {
  if (stage.kind === "HERO") return stage;
  const available = ranksForMode(mode);
  return available.includes(stage.rank) ? stage : { kind: "RANK", rank: available[0] };
}

export function stepIntroStage(stage: IntroStage, mode: ExamMode, direction: IntroDirection): IntroStep {
  if (stage.kind === "HERO") {
    return direction === "DOWN"
      ? { stage: firstRankStage(mode), boundary: "NONE" }
      : { stage, boundary: "BLOCKED_BEFORE" };
  }

  const available = ranksForMode(mode);
  const index = available.indexOf(stage.rank);
  const safeIndex = index < 0 ? 0 : index;

  if (direction === "UP") {
    return safeIndex === 0
      ? { stage: { kind: "HERO" }, boundary: "NONE" }
      : { stage: { kind: "RANK", rank: available[safeIndex - 1] }, boundary: "NONE" };
  }

  return safeIndex === available.length - 1
    ? { stage, boundary: "EXIT_AFTER" }
    : { stage: { kind: "RANK", rank: available[safeIndex + 1] }, boundary: "NONE" };
}

export function stageFromHash(hash: string, mode: ExamMode): IntroStage | null {
  if (hash === "#hero" || hash === "") return { kind: "HERO" };
  if (hash === "#ranks") return firstRankStage(mode);
  return null;
}

export function transitionDuration(previous: IntroStage, next: IntroStage) {
  return previous.kind === "RANK" && next.kind === "RANK" ? 280 : 420;
}

export function shouldAcceptDiscreteInput(lastAcceptedAt: number, now: number, lockMs: number) {
  return now - lastAcceptedAt >= lockMs;
}

export function sameIntroStage(left: IntroStage, right: IntroStage) {
  return left.kind === right.kind && (left.kind === "HERO" || (right.kind === "RANK" && left.rank === right.rank));
}
